import React, { Component, ReactNode } from 'react';
import { computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import ReactTable from 'react-table';

import { Well } from 'react-bootstrap';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { ConfirmDelete } from '../../common';
import { ILoan, ILoanAccount } from '../../../interfaces';
import { OptionsStoreClass, SessionStoreClass } from '../../../stores';

import AddLoanModal from './AddLoanModal';
import { loanColumns } from './Columns';
import LoanDetailsModal from './LoanDetailsModal';

const { PERMISSIONS } = AppConstants;

const DEFAULT_SORTED = [{ desc: true, id: 'vanity_id' }];

interface IProps {
  // The account the loans belong to, which the form needs to write a new one against
  loanAccount: ILoanAccount;
  loans: ILoan[];
  // Shares the title's row, as the liens tables on the case page do
  newButton?: ReactNode;
  // An edit and a delete both write to the account's activity feed, and a delete moves what every
  // other tab reads, so the tab re-reads them the same way it does after a create.
  onLoanChanged: () => Promise<void>;
  title: string;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
  OptionsStore: OptionsStoreClass;
  SessionStore: SessionStoreClass;
}

@inject('Clients', 'OptionsStore', 'SessionStore')
@autoBindMethods
@observer
class LoansTable extends Component<IProps> {
  // The loan whose details are showing, or null when the modal is closed
  @observable private viewingLoan: null | ILoan = null;
  // The loan open in the form, and the one waiting on a delete confirmation. Separate because the
  // form's own Delete hands over to the confirmation, closing itself on the way.
  @observable private editingLoan: null | ILoan = null;
  @observable private deletingLoan: null | ILoan = null;
  @observable private isDeleting = false;

  private get injected () {
    return this.props as IInjected;
  }

  @computed
  private get canEdit () {
    return this.injected.SessionStore.userHasPermission(PERMISSIONS.CHANGE_LOAN);
  }

  @computed
  private get canDelete () {
    return this.injected.SessionStore.userHasPermission(PERMISSIONS.DELETE_LOAN);
  }

  /**
   * What the row offers beside View. A user holding both permissions -- which the few who hold either
   * almost always will -- gets Edit, the form being where Delete then lives. Delete-only skips the form
   * it could not submit and goes straight to the confirmation.
   */
  @computed
  private get manageLabel (): null | string {
    if (this.canEdit) { return 'Edit'; }
    if (this.canDelete) { return 'Delete'; }

    return null;
  }

  @computed
  private get columns () {
    return loanColumns(this.injected.OptionsStore.loanStatusOptions, this.manageLabel);
  }

  private onViewLoanClick (rowInfo: { original: ILoan }) {
    this.viewingLoan = rowInfo.original;
  }

  private onManageLoanClick (rowInfo: { original: ILoan }) {
    if (this.canEdit) {
      this.editingLoan = rowInfo.original;
      return;
    }

    this.deletingLoan = rowInfo.original;
  }

  private closeDetailsModal () {
    this.viewingLoan = null;
  }

  private closeEditModal () {
    this.editingLoan = null;
  }

  // The form asking to delete what it has open: it closes, and the confirmation takes over.
  private onRequestDelete () {
    this.deletingLoan = this.editingLoan;
    this.editingLoan = null;
  }

  private cancelDelete () {
    this.deletingLoan = null;
  }

  private async onSaved () {
    this.editingLoan = null;
    await this.props.onLoanChanged();
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  private async onConfirmDelete () {
    const { Clients } = this.injected
      , loan = this.deletingLoan
      ;

    if (!loan) { return; }

    this.isDeleting = true;

    try {
      await Clients.loans.delete(loan.id);
      this.deletingLoan = null;
      await this.props.onLoanChanged();
    }
    catch (error) {
      // Transport has already toasted the failure -- a loan with payments allocated to it is refused,
      // which is the one a user is most likely to try -- so the confirmation is left up.
      // tslint:disable-next-line no-console
      console.error('Error deleting the loan:', error);
    }
    finally {
      this.isDeleting = false;
    }
  }

  // The whole cell is the click target rather than the button inside it, as on the liens table, so
  // that the click area covers the padding the cell gives up to it. Two cells lead somewhere, so the
  // handler is picked by which one was clicked.
  private getTdProps (_state: any, rowInfo: { original: ILoan }, column: { className?: string }) {
    const handlers: { [className: string]: () => void } = {
      'manage-loan-column': () => this.onManageLoanClick(rowInfo),
      'view-loan-column': () => this.onViewLoanClick(rowInfo),
    };

    return { onClick: handlers[column.className as string] };
  }

  private renderTable () {
    const { loans } = this.props;

    // An account with no loans yet keeps the heading and its button, and says so in place of the
    // table rather than showing empty column headers.
    if (!loans.length) {
      return <div className='empty'>There are no loans for this account.</div>;
    }

    return (
      <ReactTable
        columns={this.columns}
        data={loans}
        defaultSorted={DEFAULT_SORTED}
        getTdProps={this.getTdProps}
        keyField='id'
        minRows={0}
        pageSize={loans.length}
        resizable={false}
        showPagination={false}
        sortable
      />
    );
  }

  public render () {
    const { newButton, title } = this.props;

    return (
      <Well className='loans-list well-table'>
        <h3 className='flex-between'>
          {title}
          {newButton}
        </h3>
        {this.renderTable()}

        {this.viewingLoan && (
          <LoanDetailsModal
            loan={this.viewingLoan}
            onClose={this.closeDetailsModal}
          />
        )}

        {/* The add-loan form, opened on a loan: the fields an edit offers are the ones create sets */}
        {this.editingLoan && (
          <AddLoanModal
            canDelete={this.canDelete}
            loan={this.editingLoan}
            loanAccount={this.props.loanAccount}
            onClose={this.closeEditModal}
            onRequestDelete={this.onRequestDelete}
            onSave={this.onSaved}
          />
        )}

        {this.deletingLoan && (
          <ConfirmDelete
            fixed
            loading={this.isDeleting}
            message={`Delete loan ${this.deletingLoan.vanity_id}? This cannot be undone.`}
            onClickCancel={this.cancelDelete}
            onClickDelete={this.onConfirmDelete}
            showConfirmDelete
          />
        )}
      </Well>
    );
  }
}

export default LoansTable;
